import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = [
    "creditCardForm",
    "paypalForm",
    "mainPayButton",
    "mainPayButtonText",
    "spinner",
    "paymentMethodInput",
    "requiredCreditCardInput",
    "paypalErrorMessage",
    "shipToBillingCheckbox"
  ]

  static values = {
    initiateUrl: String,
    canadaCountryName: { type: String, default: "Canada" }
  }

  connect() {
    this.togglePaymentMethod()
    this.loadCustomCheckout()
  }

  loadCustomCheckout() {
    if (window.customcheckout) {
      this.initCustomCheckout()
    } else {
      const script = document.createElement("script")
      script.src = "https://libs.na.bambora.com/customcheckout/1/customcheckout.js"
      script.onload = () => this.initCustomCheckout()
      document.head.appendChild(script)
    }
  }

  initCustomCheckout() {
    this.customCheckout = customcheckout()
    
    const options = {
      style: {
        base: {
          fontSize: '16px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        }
      }
    }

    this.customCheckout.create('card-number', options).mount('#card-number')
    this.customCheckout.create('cvv', options).mount('#card-cvv')
    this.customCheckout.create('expiry', options).mount('#card-expiry')

    // Add listeners for validation if needed, for now just basic setup
    this.customCheckout.on('brand', (event) => {
        // Handle brand detection if desired
    })
  }

  async submitForm(event) {
    event.preventDefault()
    
    this.mainPayButtonTarget.disabled = true
    this.mainPayButtonTextTarget.classList.add('d-none')
    this.spinnerTarget.classList.remove('d-none')
    this.clearErrors()

    const selectedMethod = this.paymentMethodInputTargets.find(radio => radio.checked)?.value

    if (selectedMethod === 'credit_card') {
      this.customCheckout.createToken((result) => {
        if (result.error) {
          this.displayError(result.error.message)
          this.resetButtonState()
        } else {
          this.processPayment(result.token)
        }
      })
    } else {
      this.processPayment()
    }
  }

  async processPayment(token = null) {
    const form = this.element.querySelector("#checkout-form")
    const formData = new FormData(form)
    
    if (token) {
      formData.append("payment_token", token)
    }

    const response = await post(this.initiateUrlValue, {
      body: formData,
      responseKind: "json"
    })

    if (response.ok) {
      const data = await response.json
      if (data.redirect_url) {
        window.location.href = data.redirect_url
      }
    } else {
      const data = await response.json
      this.displayError(data.error || "An unexpected error occurred.")
      this.resetButtonState()
    }
  }

  resetButtonState() {
    this.mainPayButtonTarget.disabled = false
    this.mainPayButtonTextTarget.classList.remove('d-none')
    this.spinnerTarget.classList.add('d-none')
  }

  clearErrors() {
    const errorContainer = document.getElementById('credit-card-error-message')
    if (errorContainer) errorContainer.textContent = ''
    if (this.hasPaypalErrorMessageTarget) this.paypalErrorMessageTarget.textContent = ''
    
    // Clear specific field errors
    document.getElementById('card-number-error').textContent = ''
    document.getElementById('card-expiry-error').textContent = ''
    document.getElementById('card-cvv-error').textContent = ''
  }

  displayError(message) {
    const selectedMethod = this.paymentMethodInputTargets.find(radio => radio.checked)?.value
    
    if (selectedMethod === 'paypal' && this.hasPaypalErrorMessageTarget) {
        this.paypalErrorMessageTarget.textContent = message
    } else {
        let errorContainer = document.getElementById('credit-card-error-message')
        if (!errorContainer) {
          errorContainer = document.createElement('div')
          errorContainer.id = 'credit-card-error-message'
          errorContainer.className = 'alert alert-danger mt-3'
          this.creditCardFormTarget.prepend(errorContainer)
        }
        errorContainer.textContent = message
    }
  }

  handleCurrencyChange() {
    // No-op for redirect flow
  }

  togglePaymentMethod() {
    const selectedMethod = this.paymentMethodInputTargets.find(radio => radio.checked)?.value

    this.clearErrors()
    
    if (selectedMethod === 'credit_card') {
      this.creditCardFormTarget.classList.remove('d-none')
      this.paypalFormTarget.classList.add('d-none')
      this.requiredCreditCardInputTargets.forEach(field => field.required = true)
      this.mainPayButtonTextTarget.textContent = "Pay with Credit Card"
    } else if (selectedMethod === 'paypal') {
      this.creditCardFormTarget.classList.add('d-none')
      this.paypalFormTarget.classList.remove('d-none')
      this.requiredCreditCardInputTargets.forEach(field => field.required = false)
      this.mainPayButtonTextTarget.textContent = "Pay with PayPal"
    }
    // Ensure main button is visible for both
    this.mainPayButtonTarget.classList.remove('d-none')
  }
}